import { useEffect, useRef, useState } from "react";
import "fine-uploader";
import qq from "fine-uploader/lib/core/s3.js";
import SimpleContent from "../../../Structure/ModalManager/SimpleContent.js";
import Button from "components/Elements/Button/Button.js";
import handleUpdateFormDetailsOutside from "./handleUpdateFormDetailsOutside.js";
import LoadingContainerElement from "components/Elements/LoadingContainerElement/LoadingContainerElement.js";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import config from "config";
import ChangeChunkSizeModal from "../Modals/ChangeChunkSizeModal.tsx";
import {
  createEmptyMail,
  fileUploadFailed,
  uploadCompleteHandler,
} from "./uploaderAPI.js";
import { sleep } from "components/Pages/Uploader/Helpers/sleep.js";
import { DefaultContainerNoClose } from "components/Structure/ModalManager/DefaultContainer.js";
import { ConnectionUploaderIssue } from "../../Uploader/ModalsAndPopups/ConnectionUploaderIssue.js";
import { GetUploadEndpointApiRequest } from "../../../../api/MailsController.ts";
import { useBeforeunload } from "react-beforeunload";
import { useModal } from "../../../../contexts/modalContext/modalContext.js";
import { useContent } from "../../../../contexts/contentContext/contentContext.js";
import { useUser } from "../../../../contexts/userContext/userContext.js";
import { useUploader } from "../../../../contexts/uploaderContext/uploaderContext.js";
import { useUploaderErrorRedirect } from "./useUploaderErrorRedirect.js";
import { useCookie } from "contexts/cookieContext/cookieContext.js";
import { useToasts } from "react-toast-notifications";
import { useRecaptcha } from "contexts/recaptchaContext/recaptchaContext.js";

import "../UploaderComponent/uploader.scss";

export const useUploaderLogic = ({
  isEmbed = false,
  reuploadEmail = null,
  setFinishUpload = null,
} = {}) => {
  const {
    getIsUppy,
    setUploader,
    uploaderGetFiles,
    uploaderPauseAll,
    uploaderResumeAll,
    getUploadId,
    setUploading,
    setSize,
    getMailId,
    getToken,
    setMailId,
    setToken,
    getJumboCode,
    uploaderCancelAll,
    getHideSubmit,
    getUploading,
    uploaderRemoveFile,
    setHideSubmit,
    setExpireAfter,
  } = useUploader();

  const {
    getTextByKey,
    getLanguageCulture,
    getPath,
    getBasicSettingsData,
    getRecaptchaRequired,
  } = useContent();

  const { setShowRecaptcha } = useRecaptcha();

  const { getUserData, isSubscribed } = useUser();

  const { setModal, closeModal, getModal } = useModal();

  const { setCookies, removeCookies } = useCookie();

  const { addToast } = useToasts();

  // totalFiles state count
  const [totalFiles, setTotalFiles] = useState(0);
  // itsDone for handeling the upload complete and close-page preventing
  const [itsDone, setItsDone] = useState(false);
  // handeling list of uploaded files
  const [uploadedFiles, setUploadedFiles] = useState([]);
  // handleing erros during upload process
  const [errStatus, setErrStatus] = useState(null);
  // handeling list of files that failed to upload for showing them in error modal
  const [errFilesState, setErrFilesState] = useState([]);
  // keeping progress of uploading files
  const [filesProgress, setFilesProgress] = useState({});
  // storing chunk size in state to be able to change it
  const [chunkSize, setChunkSize] = useState(26843550);

  // const resetUploader = useResetUploader();

  const oldTitle = useRef(document.title);
  const totalFilesCount = useRef(null);
  const errorFiles = [];
  // const retries = {};

  const handleRedirectModal = useUploaderErrorRedirect({
    setItsDone,
    setErrStatus,
    uploadedFiles,
    totalFiles: totalFilesCount.current,
  });

  const initUploader = async () => {
    uploaderCancelAll();
    setUploadedFiles([]);
    setErrFilesState([]);
    setFilesProgress({});
    setErrStatus(null);
    setUploading(false);
    setTotalFiles(0);
    totalFilesCount.current = null;
    document.title = oldTitle.current;
    setSize();

    const serverRes = await apiRequest("Utils", "ReactGetTime");
    if (serverRes.status !== "success") {
      setModal({
        modal: ConnectionUploaderIssue,
        props: {
          uploadId: getUploadId(),
        },
      });
      window.dataLayer.push({
        event: "serverConnectionProblems",
      });
    }

    const serverTIme = serverRes?.data;

    const endpoint = `https://${config.api.bucket}`;
    const ApiUrl = config.api.src;

    let dynamic_endpoint = false;
    const fineUploader = new qq.s3.FineUploaderBasic({
      autoUpload: false,

      request: {
        endpoint: endpoint,
        accessKey: getBasicSettingsData().s3Key,
        clockDrift: serverTIme - Date.now(),
      },

      signature: {
        // JMFORMS
        endpoint:
          ApiUrl +
          `/Mails/${
            isEmbed || reuploadEmail
              ? "SignAWSUrlForFineUploaderJumboForms?encryptedMailId="
              : "SignAWSUrlForFineUploaderReact?uploadMailId="
          }` +
          getUploadId(),
        // customHeaders: {
        //   "x-amz-date": new Date(
        //     Date.now() +
        //       serverTIme -
        //       new Date().getTime()
        //   ).toUTCString(),
        // },
        params: {
          timediff: serverTIme - new Date().getTime(),
        },
      },

      uploadSuccess: {
        // JMFORMS
        endpoint:
          ApiUrl +
          `/Mails/${
            isEmbed || reuploadEmail
              ? "FileUploadCompleteJumboforms?encryptedMailId="
              : "FileUploadCompleteHomePage?uploadMailId="
          }` +
          getUploadId(),
      },

      cors: {
        expected: true,
      },

      retry: {
        enableAuto: true,
        autoAttemptDelay: 1,
        maxAutoAttempts: 100,
      },

      resume: {
        enabled: false,
      },

      validation: {
        stopOnFirstInvalidFile: false,
      },

      workarounds: {
        iosEmptyVideos: false,
      },

      debug: false,

      multiple: true,

      chunking: {
        enabled: true,
        concurrent: {
          enabled: true,
        },
        partSize: chunkSize,
      },

      callbacks: {
        // onSubmit - event that fires when file is added to uploader
        onSubmit: (id, fileName) => {
          const fileSize = fineUploader.getSize(id);
          setSize(fileSize);

          // detect if file size is bigger than 10000 chunks * 25mb
          if (fileSize > chunkSize * 10000) {
            setModal({
              modal: ChangeChunkSizeModal,
              modalOverlayClass: "changeChunkSizeModal",
              container: DefaultContainerNoClose,
              props: {
                fileSize,
                onCancel: () => {
                  fineUploader.cancel(id);
                },
                setChunkSize: setChunkSize,
              },
            });
          }
        },

        // onUpload - event that fires when file is uploading
        onUpload: async (id, fileName) => {
          setUploading(true);

          const response = await GetUploadEndpointApiRequest({
            realEndpoint: true,
            encryptedMailId: isEmbed ? getMailId() : undefined,
            userId: 0,
          });

          let real_endpoint = response.data;

          if (response.data == "jumbomail1") {
            real_endpoint = "jumbomail1.s3-eu-west-1.amazonaws.com";
          }
          if (!real_endpoint.includes("https://")) {
            real_endpoint = "https://" + real_endpoint;
          }

          console.log(real_endpoint);
          dynamic_endpoint = true;
          fineUploader.setEndpoint(real_endpoint, id);
        },

        // onProgress - event that fires when file is uploading
        onProgress: (id, fileName, uploadedBytes, totalBytes) => {
          if (
            (uploadedBytes !== filesProgress[id] && uploadedBytes !== 0) ||
            uploadedBytes === totalBytes
          )
            setFilesProgress((prev) => ({
              ...prev,
              [id]: uploadedBytes,
            }));
        },

        // onTotalProgress - handlind progress bar
        onTotalProgress: (totalUploadedBytes, totalBytes) => {
          const progress = Math.floor((totalUploadedBytes / totalBytes) * 100);
          if (progress > 0)
            // regex to replace previos title with new one with current progress when tempalte is "... : progress%"
            document.title = document.title.replace(
              /(: )(\d{1,3})%/,
              `: ${progress}%`
            );
        },

        // onComplete - event that fires when file is uploaded
        onComplete: (id, fileName, responseJSON) => {
          if (!responseJSON.success) {
            const file = fineUploader.getFile(id);
            setErrFilesState((prev) => [...prev, id]);
            errorFiles.push(id);
            setFilesProgress((prev) => ({
              ...prev,
              [id]: "error",
            }));
            fileUploadFailed({
              uploadId: getUploadId(),
              file,
              error: "Failed to upload",
              setUploadedFiles,
            });
          } else {
            setUploadedFiles((prev) => [...prev, id]);
          }
        },

        // onAllComplete - event that fires when all files are uploaded
        onAllComplete: (succeeded, failed) => {
          setTotalFiles(succeeded.length + failed.length);
          totalFilesCount.current = succeeded.length + failed.length;
          // setModal({
          //   modal: SimpleContent,
          //   props: {
          //     image: { name: "home/files_uploaded", ext: "png" },
          //     title: getTextByKey("upload-complete-title"),
          //     message: getTextByKey("upload-complete-message"),
          //   },
          // });
        },

        // onCancel - event that fires when file removed from uploader
        onCancel: (id, fileName) => {
          const size = fineUploader.getSize(id);
          setSize(-size);
        },

        // onError - event that fires when global error occurs
        onError: (id, fileName, errorReason, xhrOrXdr) => {
          console.log("globalError", errorReason, xhrOrXdr, fileName, id);
        },

        // onUploadError - event that fires when file upload error occurs
        onUploadError: (id, fileName, xhrOrXdr, errorReason) => {},
      },
    });

    setUploader(fineUploader);
    // }
  };

  // init uploader instance and set it to state on page mount
  useEffect(() => {
    initUploader();
  }, [getIsUppy(), getUploadId(), chunkSize]);

  useEffect(() => {
    if (getUploading()) {
      sleep.prevent();
      return () => {
        sleep.allow();
      };
    }
  }, [getUploading()]);

  useBeforeunload((e) => {
    // it was getSize() > 0 instead of getUploading() but createMailFunc need to do redirect before upload starts and in case redirect we no need to prevent it
    if (getUploading() && !itsDone) {
      // PAUSE UPLOAD TESTING
      e.preventDefault();
    }
  });

  const handleOffline = () => {
    uploaderPauseAll();
    setModal({
      modal: SimpleContent,
      props: {
        title: "Your coonnection have been lost",
        message:
          "Please check your internet connection and your upload process will continue autmatically as soon as you will be online.",
      },
    });
  };

  const handleOnline = () => {
    if (!errStatus) {
      closeModal();
      uploaderResumeAll();
    }
  };

  // handeling connection lost and connection back
  useEffect(() => {
    removeCookies("NewHomePage");
    setCookies("NewHomePage", true);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  // in case errStatus appeared - show modal with suggestion to redirect to another uploader
  useEffect(() => {
    if (errFilesState.length > 0 && !getModal() && getIsUppy()) {
      handleRedirectModal();
    }
  }, [errFilesState]);

  // add /uploading to url after upload start
  useEffect(() => {
    if (
      getUploading() &&
      !window.location.href.includes("/uploading") &&
      !isEmbed &&
      !reuploadEmail
    ) {
      window.history.pushState(
        "",
        "",
        window.location.href + "/uploading/" + getMailId()
      );
    }
  }, [getUploading()]);

  // creating mailId and token on file selected
  const handleCreateMail = async (catpchaResponse) => {
    if (!catpchaResponse && getRecaptchaRequired()) {
      setShowRecaptcha("global", handleCreateMail);
      return;
    }
    setShowRecaptcha("global", false);
    const createRes = await createEmptyMail({
      uploadId: getUploadId(),
      recaptchaResponse: catpchaResponse,
    });
    if (createRes.status === "success") {
      setMailId(createRes.data.MailEncryptedId);
      setToken(createRes.data.CSRFToken);
    }
    if (createRes.data === "user-is-suspended") {
      addToast(getTextByKey("suspended-user-message-toast"), {
        appearance: "error",
      });
      uploaderCancelAll();
    }
    if (createRes.data === "CaptchaRequired") {
      setShowRecaptcha("global", handleCreateMail);
    }
  };

  useEffect(() => {
    const filesCount = uploaderGetFiles().length;

    if (filesCount === 0) {
      setHideSubmit(true);
    } else {
      setHideSubmit(false);
    }

    if (filesCount > 0 && !getMailId()) {
      handleCreateMail();
    }

    // logic that not allow to upload more than 10000 files
    if (filesCount > 9999) {
      // remove all files that are more than 10000
      uploaderGetFiles().forEach((file) => {
        if (file.id >= 10000) {
          uploaderRemoveFile(file.id);
        }
      });
      setModal({
        modal: SimpleContent,
        props: {
          title: getTextByKey("limit-oops-text"),
          message: getTextByKey("limit-files-quantity"),
        },
      });
    }
  }, [uploaderGetFiles().length]);

  // handle logic when user has default expire after days
  useEffect(() => {
    if (getUserData()?.AutoRemoveDays) {
      setExpireAfter(getUserData().AutoRemoveDays);
      return;
    }
    if (isSubscribed()) {
      setExpireAfter(7);
      return;
    }
  }, [getUserData()]);

  //ref to handle if modal was shown
  const modalWasShown = useRef(false);
  // each time when file uploaded or gotted final error - update uploadedFiles
  // when uploaded files length equal to files length - set uploading is done and send final request to the server
  useEffect(() => {
    //errFilesState - list of files that failed to upload check if its exist to show modal with notification
    if (uploadedFiles.length === totalFilesCount.current) {
      const withError = errorFiles.length > 0 || errFilesState.length > 0;
      // JMFORMS
      if (!modalWasShown.current) {
        setModal({
          modal: SimpleContent,
          container: DefaultContainerNoClose,
          props: {
            image: { name: "home/files_uploaded", ext: "png" },
            title: withError
              ? "upload-complete-with-errors-title"
              : "upload-complete-title",
            message: withError
              ? !getHideSubmit()
                ? "upload-complete-with-errors-message-editing-mail"
                : "upload-complete-with-errors-message"
              : !getHideSubmit()
              ? "upload-complete-message-editing-mail"
              : "upload-complete-message",
            actionButton: !getHideSubmit() ? (
              <Button
                onClick={() => handleUpdateFormDetailsOutside(closeModal)}
              >
                {getTextByKey("update-details")}
              </Button>
            ) : null,
            exitButton: !getHideSubmit() ? (
              <Button onClick={() => closeModal()} blank>
                {getTextByKey("continue-edit")}
              </Button>
            ) : null,
            additionalText: getHideSubmit() ? (
              <LoadingContainerElement />
            ) : null,
          },
        });
      }

      if (!getHideSubmit()) {
        modalWasShown.current = true;
      } else {
        setTimeout(
          () => {
            uploadCompleteHandler({
              files: uploaderGetFiles(),
              uploadId: getUploadId(),
              mailId: getMailId(),
              lang: getLanguageCulture(),
              userMail: getUserData().UserName,
              token: getToken(),
              langPath: getPath(),
              code: getJumboCode(),
              setItsDone,
              // JMFORMS
              setFinishUpload,
              closeModal,
              reuploadUserSenderEmail: reuploadEmail,
            });
          },
          modalWasShown.current ? 1000 : 4000
        );
      }
      if (!isEmbed) {
        setTimeout(() => {
          uploadCompleteHandler({
            files: uploaderGetFiles(),
            uploadId: getUploadId(),
            mailId: getMailId(),
            lang: getLanguageCulture(),
            userMail: getUserData().UserName,
            token: getToken(),
            langPath: getPath(),
            code: getJumboCode(),
            setItsDone,
            // JMFORMS
            setFinishUpload,
            closeModal,
            reuploadUserSenderEmail: reuploadEmail,
          });
        }, 300000);
      }
    }
  }, [uploadedFiles, getMailId(), getHideSubmit(), totalFiles]);

  return {
    filesCount: uploadedFiles.length,
    filesProgress,
  };
};
